import { supabase } from '@/integrations/supabase/client';

interface SendEncryptedMessageParams {
  recipientId: string;
  subject: string;
  message: string;
  orderId?: string;
  disputeId?: string;
  imageUrl?: string;
}

interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  order_id: string | null;
  dispute_id: string | null;
  subject: string;
  content: string;
  message?: string;
  is_read?: boolean;
  created_at: string;
  is_encrypted?: boolean;
  encrypted_subject?: string;
  encrypted_message?: string;
}

/**
 * Custom hook for encrypting and decrypting messages
 * Falls back to direct insert if edge function fails
 */
export const useMessageEncryption = () => {
  /**
   * Send a message - tries edge function first, falls back to direct insert
   */
  const sendEncryptedMessage = async (params: SendEncryptedMessageParams) => {
    try {
      // Try edge function first
      const { data, error } = await supabase.functions.invoke('encrypt-message', {
        body: params
      });

      if (!error) return { data, error: null };
      
      console.warn('Edge function failed, falling back to direct insert:', error);
    } catch (edgeFnError) {
      console.warn('Edge function error, using fallback:', edgeFnError);
    }

    // Fallback: insert directly into messages table
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Try full insert first
      const fullPayload: Record<string, any> = {
        sender_id: user.id,
        recipient_id: params.recipientId,
        subject: params.subject || 'Message',
        content: params.message,
      };
      if (params.orderId) fullPayload.order_id = params.orderId;
      if (params.imageUrl) fullPayload.image_url = params.imageUrl;

      const { data, error } = await supabase
        .from('messages')
        .insert(fullPayload)
        .select('*')
        .single();

      if (!error) return { data, error: null };

      // If 'subject' or other column missing, retry with bare minimum
      console.warn('Full insert failed, trying minimal:', error.message);
      const minPayload: Record<string, any> = {
        sender_id: user.id,
        recipient_id: params.recipientId,
        content: params.message,
      };
      if (params.orderId) minPayload.order_id = params.orderId;
      if (params.imageUrl) minPayload.image_url = params.imageUrl;

      const { data: data2, error: error2 } = await supabase
        .from('messages')
        .insert(minPayload)
        .select('*')
        .single();

      if (error2) throw error2;
      return { data: data2, error: null };
    } catch (fallbackError) {
      console.error('Fallback insert also failed:', fallbackError);
      return { data: null, error: fallbackError };
    }
  };

  /**
   * Decrypt multiple messages
   */
  const decryptMessages = async (messages: Message[]): Promise<Message[]> => {
    // Separate encrypted and non-encrypted messages
    const encryptedMessages = messages.filter(m => m.is_encrypted);
    const plainMessages = messages.filter(m => !m.is_encrypted);

    if (encryptedMessages.length === 0) {
      return messages;
    }

    try {
      const messageIds = encryptedMessages.map(m => m.id);
      
      const { data, error } = await supabase.functions.invoke('decrypt-messages', {
        body: { messageIds }
      });

      if (error) throw error;

      // Combine decrypted messages with plain messages
      const decryptedMessagesMap = new Map(
        (data?.messages as Message[] || []).map((m: Message) => [m.id, m])
      );

      return messages.map(msg => {
        if (msg.is_encrypted) {
          return decryptedMessagesMap.get(msg.id) || msg;
        }
        return msg;
      });
    } catch (error) {
      console.error('Error decrypting messages:', error);
      // Return original messages if decryption fails
      return messages;
    }
  };

  return {
    sendEncryptedMessage,
    decryptMessages,
  };
};
